// src/components/AppPhoneInput.tsx
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import PhoneInput, {
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import InputWithoutCountry from 'react-phone-number-input/input';
import 'react-phone-number-input/style.css';
import { MaskPhone } from '../../utils/FunctionsGlobals';

type PhoneInputProps = React.ComponentProps<typeof PhoneInput>;

const DIGIT_MAX = 16;
const DIGIT_MIN = 10;
const LOCAL_MASK_MAX = 15;

const sanitizePhoneNumber = (
  raw: string,
  includeCountryCode: boolean,
  limitMaxLength: boolean
) => {
  if (!raw) return '';
  if (!includeCountryCode) {
    const digits = raw.replace(/\D/g, '');
    const limited = limitMaxLength ? digits.slice(0, DIGIT_MAX) : digits;
    return limited;
  }

  const digits = raw.replace(/\D/g, '');
  const limited = limitMaxLength ? digits.slice(0, DIGIT_MAX) : digits;
  if (!limited) return '';
  return `+${limited}`;
};

const normalizeToE164 = (raw: string, defaultCountry?: string) => {
  try {
    const parsed = parsePhoneNumber(raw, defaultCountry as any);
    if (parsed && parsed.isValid()) return parsed.number;
  } catch {
    // ignore parse errors and let validation handle it
  }
  return '';
};

export interface AppPhoneInputProps
  extends Omit<PhoneInputProps, 'value' | 'onChange' | 'numberInputProps'> {
  value: string;
  onChange: (_value: string) => void;
  label?: string;
  error?: string;
  helperText?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  /** Estilos inline do input (para manter tema da pagina) */
  inputStyle?: React.CSSProperties;
  errorClassName?: string;
  width_max?: number;
  /** Altura do input (ex: 56 ou "56px") para alinhar a bandeira */
  inputHeight?: number | string;
  helperTextClassName?: string;
  /** Se true, nao mostra o select de pais, so o input */
  semPais?: boolean;
  /** Se true inclui o DDI; se false, aceita numero local sem +DDI */
  includeCountryCode?: boolean;
  /** Limita automaticamente o tamanho do numero por pais (default: true) */
  limitMaxLength?: boolean;
  /** Callback opcional pra voce saber se o numero e valido */
  onValidityChange?: (_isValid: boolean) => void;
}

export const AppPhoneInput: React.FC<AppPhoneInputProps> = ({
  value,
  onChange,
  label,
  error,
  helperText,
  containerClassName,
  labelClassName,
  inputClassName,
  inputStyle,
  width_max,
  inputHeight,
  errorClassName,
  helperTextClassName,
  semPais = false,
  includeCountryCode = true,
  limitMaxLength = true,
  onValidityChange,
  // props nativas do PhoneInput
  id,
  required,
  disabled,
  defaultCountry = 'BR',
  international = true,
  numberInputProps,
  countryCallingCodeEditable = false,
  placeholder,
  autoComplete,
  ...rest
}) => {
  const noCountrySelect = semPais || !includeCountryCode;
  const helperId = helperText ? `${id || 'phone'}-helper` : undefined;
  const errorId = error ? `${id || 'phone'}-error` : undefined;

  const handleChange = (newValue: string | undefined) => {
    const sanitized = sanitizePhoneNumber(
      newValue || '',
      includeCountryCode,
      limitMaxLength
    );
    onChange(sanitized);
    if (onValidityChange) {
      const onlyDigits = sanitized.replace(/\D/g, '');
      const valid = includeCountryCode
        ? isValidPhoneNumber(sanitized)
        : onlyDigits.length >= DIGIT_MIN && onlyDigits.length <= DIGIT_MAX;
      onValidityChange(valid);
    }
  };

  const handleBlur = () => {
    if (!value) {
      onValidityChange?.(false);
      return;
    }
    if (includeCountryCode) {
      const normalized = normalizeToE164(value, defaultCountry);
      if (normalized) {
        onChange(normalized);
        onValidityChange?.(true);
      } else {
        onChange('');
        onValidityChange?.(false);
      }
      return;
    }
    const cleaned = sanitizePhoneNumber(value, false, limitMaxLength);
    onChange(cleaned);
    const digits = cleaned.replace(/\D/g, '');
    onValidityChange?.(
      digits.length >= DIGIT_MIN && digits.length <= DIGIT_MAX
    );
  };

  const inputClasses = ['app-phone-input__input', inputClassName]
    .filter(Boolean)
    .join(' ');

  const displayValue =
    !includeCountryCode && noCountrySelect
      ? MaskPhone(value || '')
      : value || '';

  // avisa pro pai se o numero e valido sempre que mudar
  useEffect(() => {
    if (!onValidityChange) return;
    if (!value) {
      onValidityChange(false);
    } else {
      const onlyDigits = value.replace(/\D/g, '');
      const valid = includeCountryCode
        ? isValidPhoneNumber(value)
        : onlyDigits.length >= DIGIT_MIN && onlyDigits.length <= DIGIT_MAX;
      onValidityChange(valid);
    }
  }, [value, includeCountryCode, onValidityChange]);

  const mergedNumberInputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    ...(numberInputProps as React.InputHTMLAttributes<HTMLInputElement>),
    className: [(numberInputProps as any)?.className, inputClasses]
      .filter(Boolean)
      .join(' '),
    style: {
      ...(numberInputProps as any)?.style,
      ...inputStyle,
    },
    inputMode: 'tel',
    'aria-invalid': !!error,
    'aria-describedby': error ? errorId : helperId,
    onBlur: (event) => {
      numberInputProps?.onBlur?.(event);
      handleBlur();
    },
    maxLength: limitMaxLength ? DIGIT_MAX + 1 : undefined,
  };

  const plainNumberInputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    id,
    className: inputClasses,
    disabled,
    required,
    placeholder,
    autoComplete,
    value: displayValue,
    inputMode: 'tel',
    type: 'tel',
    'aria-invalid': !!error,
    'aria-describedby': error ? errorId : helperId,
    style: inputStyle,
    maxLength: limitMaxLength ? LOCAL_MASK_MAX : undefined,
    onBlur: handleBlur,
    onChange: (event) => handleChange(event.target.value),
  };

  // Modal de selecao de pais customizada
  const CountrySelectComponent: React.FC<any> = (selectProps) => {
    const {
      value: countryValue,
      onChange: onCountryChange,
      options,
      iconComponent: Icon,
      className,
    } = selectProps;

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => {
      if (!options) return [];
      const q = search.trim().toLowerCase();
      if (!q) return options;
      return options.filter((opt: any) => {
        const label = (opt.label || '').toLowerCase();
        const val = (opt.value || '').toLowerCase();
        return label.includes(q) || val.includes(q);
      });
    }, [options, search]);

    const selected =
      options?.find((opt: any) => opt.value === countryValue) || options?.[0];

    const portalOverlay = open ? (
      <div
        className="app-phone-input__modal-overlay"
        onClick={() => setOpen(false)}
      >
        <div
          className="app-phone-input__modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="app-phone-input__modal-header">
            <h4>Escolha o país</h4>
            <button
              type="button"
              className="app-phone-input__modal-close"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="app-phone-input__modal-search">
            <input
              type="text"
              placeholder="Busque pelo nome ou código"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="app-phone-input__modal-list">
            {filtered?.map((opt: any) => (
              <button
                type="button"
                key={opt.value || opt.label}
                className="app-phone-input__modal-item"
                onClick={() => {
                  onCountryChange?.(opt.value);
                  setOpen(false);
                  setSearch('');
                }}
              >
                {Icon && opt.value && (
                  <span className="app-phone-input__modal-flag">
                    <Icon
                      country={opt.value}
                      label={opt.label || opt.value || 'País'}
                      countryName={opt.label || opt.value || 'País'}
                    />
                  </span>
                )}
                <span className="app-phone-input__modal-label">
                  {opt.label}
                </span>
                {opt.value && (
                  <span className="app-phone-input__modal-ddd">
                    +{getCountryCallingCode(opt.value)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    ) : null;

    return (
      <>
        <button
          type="button"
          className={['app-phone-input__country-trigger', className]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setOpen(true)}
        >
          {Icon && selected?.value && (
            <span className="app-phone-input__country-flag">
              <Icon
                country={selected.value}
                label={selected.label || selected.value || 'País'}
                countryName={selected.label || selected.value || 'País'}
              />
            </span>
          )}
          <span className="app-phone-input__country-arrow">▼</span>
          <span className="app-phone-input__country-sep" aria-hidden="true" />
        </button>

        {typeof document !== 'undefined' && portalOverlay
          ? ReactDOM.createPortal(portalOverlay, document.body)
          : portalOverlay}
      </>
    );
  };

  const containerStyle: React.CSSProperties = {
    ...(width_max ? { maxWidth: `${width_max}%` } : {}),
    ...((inputHeight !== undefined && inputHeight !== null)
      ? { ['--app-phone-input-height' as any]: typeof inputHeight === 'number' ? `${inputHeight}px` : inputHeight }
      : {}),
  };

  return (
    <div
      className={[
        'app-phone-input',
        containerClassName,
        noCountrySelect && 'app-phone-input--no-country',
        error && 'app-phone-input--error',
      ]
        .filter(Boolean)
        .join(' ')}
      style={containerStyle}
    >
      {label && (
        <label
          className={['app-phone-input__label', labelClassName]
            .filter(Boolean)
            .join(' ')}
          htmlFor={id}
        >
          {label}
        </label>
      )}

      <div className="app-phone-input__control-wrapper">
        {noCountrySelect ? (
          includeCountryCode ? (
            <InputWithoutCountry
              id={id}
              className={inputClasses}
              disabled={disabled}
              required={required}
              country={defaultCountry}
              international={includeCountryCode ? international : false}
              withCountryCallingCode={includeCountryCode}
              limitMaxLength={limitMaxLength}
              placeholder={placeholder}
              autoComplete={autoComplete}
              value={value || ''}
              inputMode="tel"
              aria-invalid={!!error}
              aria-describedby={error ? errorId : helperId}
              style={inputStyle}
              onBlur={handleBlur}
              onChange={(newValue: string | undefined) => {
                handleChange(newValue);
              }}
            />
          ) : (
            <input {...plainNumberInputProps} />
          )
        ) : (
          <PhoneInput
            id={id}
            disabled={disabled}
            required={required}
            className="app-phone-input__control"
            defaultCountry={includeCountryCode ? defaultCountry : undefined}
            international={includeCountryCode ? international : false}
            withCountryCallingCode={includeCountryCode}
            placeholder={placeholder}
            autoComplete={autoComplete}
            value={value || undefined}
            onChange={handleChange}
            numberInputProps={mergedNumberInputProps}
            flags={flags}
            limitMaxLength={limitMaxLength}
            countryCallingCodeEditable={countryCallingCodeEditable}
            countrySelectComponent={CountrySelectComponent}
            {...rest}
          />
        )}
      </div>

      {error ? (
        <p
          className={['app-phone-input__error', errorClassName]
            .filter(Boolean)
            .join(' ')}
          id={errorId}
        >
          {error}
        </p>
      ) : helperText ? (
        <p
          className={['app-phone-input__helper', helperTextClassName]
            .filter(Boolean)
            .join(' ')}
          id={helperId}
        >
          {helperText}
        </p>
      ) : null}
    </div>
  );
};
